<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { MapPin, Trash2, Plus, ShoppingBag } from 'lucide-vue-next';
import UserAddressDialog from '@/widgets-iframe/user-address-form/UserAddressDialog.vue';
import type { AddressType } from '@lib/zod/userAddressSchema';
import { initState, courierRequestState_perOrderRef } from './state';
import { type storeOrdersApiClient } from '@api-client/store_orders';
import type { ResType } from '@api-client/typeUtils';
type OrderDetailsType = ResType<typeof storeOrdersApiClient[":storeId"]['get_by_order_ids']['$post']>["data"][number];


const props = defineProps<{
    orderDetails: OrderDetailsType;
}>();

onMounted(async () => {
    initState(props.orderDetails);
});

onUnmounted(() => {
    console.log('delete state for', props.orderDetails.storeOrderId);
    delete courierRequestState_perOrderRef.value[props.orderDetails.storeOrderId];
});

function addManifestItem() {
    courierRequestState_perOrderRef.value[props.orderDetails.storeOrderId].manifestItems.push({ name: '', quantity: 1 });
}

function removeManifestItem(index: number) {
    courierRequestState_perOrderRef.value[props.orderDetails.storeOrderId].manifestItems.splice(index, 1);
}


const isEditPickupOpen = ref(false);
const isEditDeliveryOpen = ref(false);
function onPickupSaved(addr: AddressType) {
    courierRequestState_perOrderRef.value[props.orderDetails.storeOrderId].pickupAddress = addr;
    isEditPickupOpen.value = false;
    // Invalidate stale quote
}
function onDeliverySaved(addr: AddressType) {
    courierRequestState_perOrderRef.value[props.orderDetails.storeOrderId].deliveryAddress = addr;
    isEditDeliveryOpen.value = false;
}

function formatAddress(a: AddressType | null) {
    if (!a) return '';
    return [
        a.address1,
        a.address2,
        [a.city, a.state].filter(Boolean).join(', '),
        a.postalCode,
        a.country,
    ].filter(Boolean).join(' · ');
}

</script>

<template>
    <div v-if="courierRequestState_perOrderRef[props.orderDetails.storeOrderId]">
        <div class="space-y-4 p-2">
            <Card>
                <CardContent class="p-4">
                    <div class="flex items-center justify-between mb-2">
                        <h3 class="font-bold text-lg flex items-center gap-2">
                            <MapPin class="h-4 w-4" />
                            Pickup
                        </h3>
                        <Button variant="outline"
                                size="sm"
                                @click="isEditPickupOpen = true">
                            Edit
                        </Button>
                    </div>
                    <div v-if="courierRequestState_perOrderRef[props.orderDetails.storeOrderId].pickupAddress"
                         class="text-sm">
                        <div class="font-medium">
                            {{ courierRequestState_perOrderRef[props.orderDetails.storeOrderId].pickupAddress?.shippingName }}
                        </div>
                        <div class="text-muted-foreground">
                            {{ formatAddress(courierRequestState_perOrderRef[props.orderDetails.storeOrderId].pickupAddress) }}
                        </div>
                        <div v-if="courierRequestState_perOrderRef[props.orderDetails.storeOrderId].pickupAddress?.phoneNumber"
                             class="text-muted-foreground">☎️
                            {{ courierRequestState_perOrderRef[props.orderDetails.storeOrderId].pickupAddress?.phoneNumber }}
                        </div>
                    </div>
                    <div v-else
                         class="text-destructive text-sm">No pickup address</div>
                </CardContent>
            </Card>

            <Card>
                <CardContent class="p-4">
                    <div class="flex items-center justify-between mb-2">
                        <h3 class="font-bold text-lg flex items-center gap-2">
                            <MapPin class="h-4 w-4" />
                            Delivery
                        </h3>
                        <Button variant="outline"
                                size="sm"
                                @click="isEditDeliveryOpen = true">
                            Edit
                        </Button>
                    </div>
                    <div v-if="courierRequestState_perOrderRef[props.orderDetails.storeOrderId].deliveryAddress"
                         class="text-sm">
                        <div class="font-medium">
                            {{ courierRequestState_perOrderRef[props.orderDetails.storeOrderId].deliveryAddress?.shippingName }}
                        </div>
                        <div class="text-muted-foreground">
                            {{ formatAddress(courierRequestState_perOrderRef[props.orderDetails.storeOrderId].deliveryAddress) }}
                        </div>
                        <div v-if="courierRequestState_perOrderRef[props.orderDetails.storeOrderId].deliveryAddress?.phoneNumber"
                             class="text-muted-foreground">☎️
                            {{ courierRequestState_perOrderRef[props.orderDetails.storeOrderId].deliveryAddress?.phoneNumber }}
                        </div>
                        <div v-if="courierRequestState_perOrderRef[props.orderDetails.storeOrderId].deliveryAddress?.instructionsForPickupDelivery"
                             class="text-muted-foreground italic mt-1">
                            {{ courierRequestState_perOrderRef[props.orderDetails.storeOrderId].deliveryAddress?.instructionsForPickupDelivery }}
                        </div>
                    </div>
                    <div v-else
                         class="text-destructive text-sm">No delivery address</div>
                </CardContent>
            </Card>

            <Card>
                <CardContent class="p-4">

                    <details>
                        <summary>Advanced Options</summary>
                        <div class="space-y-2">
                            <div class="mb-3">
                                <label class="text-sm font-medium">Tip for courier (100% of buyer's tip is passed
                                    through)</label>
                                <Input v-model.number="courierRequestState_perOrderRef[props.orderDetails.storeOrderId].tipCourier"
                                       type="number"
                                       :min="0"
                                       :step="1"
                                       class="mt-1" />
                            </div>
                            <div class="mb-2">
                                <label class="text-sm font-medium">Dropoff seller notes</label>
                                <Input v-model="courierRequestState_perOrderRef[props.orderDetails.storeOrderId].dropoffSellerNotes"
                                       maxlength="280"
                                       placeholder="Notes for courier when dropping off (optional)"
                                       class="mt-1" />
                                <div class="text-xs text-muted-foreground mt-1">
                                    {{ courierRequestState_perOrderRef[props.orderDetails.storeOrderId].dropoffSellerNotes.length }}/280
                                </div>
                            </div>
                            <div class="mb-2">
                                <label class="text-sm font-medium">Return notes</label>
                                <Input v-model="courierRequestState_perOrderRef[props.orderDetails.storeOrderId].returnNotes"
                                       maxlength="280"
                                       placeholder="If undeliverable, instructions for returning to store (optional)"
                                       class="mt-1" />
                                <div class="text-xs text-muted-foreground mt-1">
                                    {{ courierRequestState_perOrderRef[props.orderDetails.storeOrderId].returnNotes.length }}/280
                                </div>
                            </div>
                            <div class="mb-2">
                                <label for="undeliverable-action"
                                       class="text-sm font-medium">Undeliverable action</label>
                                <select id="undeliverable-action"
                                        v-model="courierRequestState_perOrderRef[props.orderDetails.storeOrderId].undeliverableAction"
                                        class="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background">
                                    <option value="leave_at_door">leave_at_door</option>
                                    <option value="return">return</option>
                                    <option value="discard">discard</option>
                                </select>
                            </div>
                        </div>
                    </details>



                    <details class="mt-8">
                        <summary>Items</summary>
                        <div class="space-y-2">
                            <div class="flex items-center justify-between mb-2">
                                <h3 class="font-bold text-lg flex items-center gap-2">
                                    <ShoppingBag class="h-4 w-4" />
                                    Manifest Items ({{
                                        courierRequestState_perOrderRef[props.orderDetails.storeOrderId].manifestItems.length
                                    }})
                                </h3>
                                <Button variant="outline"
                                        size="sm"
                                        @click="addManifestItem">
                                    <Plus class="h-4 w-4 mr-1" />
                                    Add
                                </Button>
                            </div>
                            <div v-if="courierRequestState_perOrderRef[props.orderDetails.storeOrderId].manifestItems.length === 0"
                                 class="text-sm text-muted-foreground">
                                No items. Add at least one item.
                            </div>
                            <div v-for="(item, index) in courierRequestState_perOrderRef[props.orderDetails.storeOrderId].manifestItems"
                                 :key="index"
                                 class="flex items-center gap-2 py-1">
                                <Input v-model="item.name"
                                       placeholder="Item name"
                                       class="flex-1" />
                                <Input v-model.number="item.quantity"
                                       type="number"
                                       min="1"
                                       placeholder="Qty"
                                       class="w-20" />
                                <Button variant="ghost"
                                        size="icon"
                                        @click="removeManifestItem(index)">
                                    <Trash2 class="h-4 w-4 text-destructive" />
                                </Button>
                            </div>
                        </div>
                    </details>

                </CardContent>
            </Card>
        </div>
        <UserAddressDialog v-if="isEditPickupOpen"
                           title="Edit Pickup Address"
                           :address="courierRequestState_perOrderRef[props.orderDetails.storeOrderId].pickupAddress ?? undefined"
                           @close="isEditPickupOpen = false"
                           @addressFormSubmitted="onPickupSaved" />
        <UserAddressDialog v-if="isEditDeliveryOpen"
                           title="Edit Delivery Address"
                           :address="courierRequestState_perOrderRef[props.orderDetails.storeOrderId].deliveryAddress ?? undefined"
                           @close="isEditDeliveryOpen = false"
                           @addressFormSubmitted="onDeliverySaved" />
    </div>
</template>
